import type { GeneratedAssetKind } from '@/types/generatedAsset'

import {
  HTML_FENCE_GLOBAL_RE,
  HTML_OR_MERMAID_PREVIEW_FENCE_RE,
  HTML_PREVIEW_FENCE_INDEX_RE,
  MERMAID_FENCE_RE,
} from './constants'

import type { PreviewContent } from './types'

export function isSamePreviewSession(
  prev: PreviewContent | null,
  next: PreviewContent,
): boolean {
  if (!prev || prev.type !== next.type) return false
  if (prev.type === 'spreadsheet') {
    return next.type === 'spreadsheet' && prev.title === next.title
  }
  if (prev.type === 'powerpoint') {
    return next.type === 'powerpoint' && prev.title === next.title
  }
  if (prev.type === 'pdf') {
    return next.type === 'pdf' && prev.title === next.title
  }
  return (
    (next.type === 'html' || next.type === 'mermaid') && prev.raw === next.raw
  )
}

/**
 * Which generated-asset card, if any, a fenced code block's language maps to.
 * `undefined` means the fence stays a plain code block.
 */
export function detectContentType(
  language: string,
): GeneratedAssetKind | undefined {
  const lang = language.toLowerCase()
  if (lang === 'html' || lang === 'mermaid') return lang

  return undefined
}

export type HtmlPreviewViewMode = 'preview' | 'code'

/** True once the streamed HTML document includes a closing `</html>` tag. */
export function isHtmlGenerationComplete(html: string): boolean {
  return /<\/html>/i.test(html)
}

/**
 * Is a generated HTML block still streaming in?
 *
 * `isHtmlGenerationComplete` on its own only reads the content's shape, so anything
 * that never produces a `</html>` reads as generating forever: an aborted or errored
 * stream, a page the model truncated, a plain HTML fragment. That leaves the asset
 * card glowing with its actions hidden, and survives a reload — the truncated markdown
 * is persisted, so the block comes back stuck.
 *
 * Two signals settle it, checked before falling back to the content shape:
 *
 * - `isFenceClosed` — the model emitted this block's closing ``` , so nothing more is
 *   coming for it whatever the HTML looks like.
 * - `isChatResponding` — nothing is streaming into the chat at all, so no chunk can be
 *   appended to any block in it. False on a freshly loaded chat, which is what unsticks
 *   a truncated page read back from history.
 */
export function isHtmlBlockGenerating(options: {
  raw: string
  isFenceClosed: boolean
  isChatResponding: boolean
}): boolean {
  if (isHtmlGenerationComplete(options.raw)) return false
  if (options.isFenceClosed) return false
  return options.isChatResponding
}

/**
 * Is a generated asset block still streaming in? Generalises
 * `isHtmlBlockGenerating` across every fence kind the chat renders as a card.
 *
 * Only HTML carries a completion marker in its content (`</html>`). A Mermaid
 * diagram ends wherever the model stops, so it rests on the two stream signals
 * alone — which is exactly the fallback the HTML path already relies on for
 * fragments and truncated pages.
 */
export function isGeneratedAssetGenerating(options: {
  kind: GeneratedAssetKind
  raw: string
  isFenceClosed: boolean
  isChatResponding: boolean
}): boolean {
  if (options.kind === 'html') return isHtmlBlockGenerating(options)
  if (options.isFenceClosed) return false
  return options.isChatResponding
}

/**
 * Did a generated asset block stop before the model finished it? The settled
 * counterpart of {@link isGeneratedAssetGenerating}: same signals, read once nothing
 * is streaming into the chat. A fence that never closed and, for HTML, no `</html>`
 * means the stream was interrupted, stopped by the user, or cut at the output-token
 * limit — the persisted markdown keeps the cut, so this holds after a reload too.
 *
 * A closed fence is always complete, which keeps HTML fragments (no `</html>` by
 * design) out of it; a full document whose closing fence alone went missing is
 * complete as well.
 */
export function isGeneratedAssetIncomplete(options: {
  kind: GeneratedAssetKind
  raw: string
  isFenceClosed: boolean
  isChatResponding: boolean
}): boolean {
  if (options.isChatResponding || options.isFenceClosed) return false
  return options.kind !== 'html' || !isHtmlGenerationComplete(options.raw)
}

/**
 * Drop anything the model appended after the document's closing `</html>` — replies
 * routinely continue with prose ("I can also provide…"). That text isn't part of the
 * page, but the HTML parser folds whatever follows `</html>` back into `<body>`, so
 * it would render inside the preview iframe and get published verbatim. Clips to the
 * LAST `</html>` (a page teaching HTML may contain an earlier literal one in its body).
 *
 * No-op while the closing tag hasn't streamed in yet, or for HTML fragments that have
 * no `</html>` at all — streaming/partial previews are left untouched.
 */
export function clipTrailingAfterHtmlClose(html: string): string {
  const closeRe = /<\/html\s*>/gi
  let lastEnd = -1
  let match: RegExpExecArray | null
  while ((match = closeRe.exec(html)) !== null) {
    lastEnd = match.index + match[0].length
  }
  return lastEnd === -1 ? html : html.slice(0, lastEnd)
}

/**
 * Prose some models append after `</html>` inside a ```html fence. Kept out of the
 * preview/publish HTML but shown in chat below the AI Asset card.
 */
export function extractTrailingProseAfterHtmlClose(html: string): string {
  const closeRe = /<\/html\s*>/gi
  let lastEnd = -1
  let match: RegExpExecArray | null
  while ((match = closeRe.exec(html)) !== null) {
    lastEnd = match.index + match[0].length
  }
  if (lastEnd === -1) return ''
  return html.slice(lastEnd).trim()
}

/**
 * Whitespace the API's `extract_html_blocks` treats as fence padding
 * (`_WS = " \t\n\r\f\v"`), reproduced so the FE splits blocks byte-for-byte the way
 * the backend does when it materialises / publishes `html_block_{n}.html`.
 */
const FENCE_WS = new Set([' ', '\t', '\n', '\r', '\f', '\v'])

/**
 * Offset just past the opening ```html fence line, or null when the marker isn't a
 * real fence open. Faithful port of the API's `_consume_html_fence_open_suffix`: only
 * whitespace may follow ```html up to the first newline; any other char disqualifies it.
 */
function htmlFenceBodyStart(content: string, after: number): number | null {
  for (let mid = after; mid < content.length; mid += 1) {
    const ch = content[mid]
    if (ch === '\n') return mid + 1
    if (!FENCE_WS.has(ch)) return null
  }
  return null
}

export type RawHtmlBlock = {
  raw: string
  blockIndex: number
  /** Offset just past the block's closing ``` (or end of string when unclosed). */
  end: number
}

/**
 * Split `content` into ```html blocks the exact way evergpt-api's `extract_html_blocks`
 * does (`chat_response_published_html.py`): a case-insensitive ```html marker whose
 * opening line holds only whitespace, a body that runs to the NEXT ``` **anywhere**
 * (not just at a line start), and — when the fence is left open — a final block that
 * runs to end-of-string (trailing whitespace trimmed, as the backend `rstrip()`s).
 *
 * Keeping this identical to the API is what guarantees the block a user previews /
 * publishes is the same block, at the same `blockIndex`, that the backend serves for
 * `POST /publishing/html/{chat}/{message}/{blockIndex}`. `raw` is returned as the
 * backend produces it (untrimmed for closed blocks); callers clip/trim for display.
 */
export function scanHtmlBlocks(content: string): RawHtmlBlock[] {
  const marker = '```html'
  const lower = content.toLowerCase()
  const blocks: RawHtmlBlock[] = []
  let idx = 0
  for (;;) {
    const start = lower.indexOf(marker, idx)
    if (start === -1) break
    const bodyStart = htmlFenceBodyStart(content, start + marker.length)
    if (bodyStart === null) {
      idx = start + 1
      continue
    }
    const close = lower.indexOf('```', bodyStart)
    if (close === -1) {
      blocks.push({
        raw: content.slice(bodyStart).replace(/\s+$/, ''),
        blockIndex: blocks.length,
        end: content.length,
      })
      break
    }
    blocks.push({
      raw: content.slice(bodyStart, close),
      blockIndex: blocks.length,
      end: close + '```'.length,
    })
    idx = close + '```'.length
  }
  return blocks
}

/**
 * ```html block bodies exactly as the backend's `extract_html_blocks` returns them —
 * the source-of-truth split used for `blockIndex` ↔ published-file parity. Not clipped
 * or trimmed; use `extractAllHtmlPreviewBlocks` for the preview/selector variant.
 */
export function extractHtmlBlocks(content: string): string[] {
  return scanHtmlBlocks(content).map((block) => block.raw)
}

export type HtmlPreviewBlock = {
  raw: string
  end: number
  blockIndex: number
}

/**
 * Latest ```html``` block by end position, including an open fence while streaming.
 * Boundaries and `blockIndex` come from `scanHtmlBlocks`, so they match the backend
 * (and the publish endpoint's block index). Blocks are in document order, so the last
 * one is the latest.
 */
export function extractLatestHtmlPreviewBlockStreaming(
  markdown: string,
): HtmlPreviewBlock | null {
  const blocks = scanHtmlBlocks(markdown)
  if (!blocks.length) return null
  const last = blocks[blocks.length - 1]
  return { raw: last.raw, end: last.end, blockIndex: last.blockIndex }
}

/** Last ```mermaid … ``` body in the string (if several, uses the last — typically the model’s correction). */
export function extractLastMermaidCodeBlock(markdown: string): string | null {
  let last: string | null = null
  let match: RegExpExecArray | null
  MERMAID_FENCE_RE.lastIndex = 0
  while ((match = MERMAID_FENCE_RE.exec(markdown)) !== null) {
    const chunk = match[1]?.trim()
    if (chunk) last = chunk
  }
  return last
}

/**
 * Latest **closed** ```html``` or ```mermaid``` fence in document order (by end index).
 * Incomplete fences while streaming are ignored until the closing ``` arrives.
 * Never returns spreadsheet / PowerPoint preview shapes.
 */
export function extractLatestPreviewBlockFromMarkdown(
  markdown: string,
  options: { htmlEnabled: boolean },
): { type: 'html' | 'mermaid'; raw: string } | null {
  type Latest = { type: 'html' | 'mermaid'; raw: string; end: number }
  let latest: Latest | null = null

  const takeIfLater = (
    end: number,
    block: Pick<Latest, 'type' | 'raw'>,
  ): void => {
    if (!latest || end > latest.end) latest = { ...block, end }
  }

  if (options.htmlEnabled) {
    const streamingHtml = extractLatestHtmlPreviewBlockStreaming(markdown)
    if (streamingHtml?.raw.trim()) {
      takeIfLater(streamingHtml.end, {
        type: 'html',
        raw: streamingHtml.raw,
      })
    }

    for (const m of markdown.matchAll(MERMAID_FENCE_RE)) {
      const raw = m[1]?.trim()
      if (!raw) continue
      takeIfLater((m.index ?? 0) + m[0].length, { type: 'mermaid', raw })
    }
  } else {
    for (const m of markdown.matchAll(MERMAID_FENCE_RE)) {
      const raw = m[1]?.trim()
      if (!raw) continue
      takeIfLater((m.index ?? 0) + m[0].length, { type: 'mermaid', raw })
    }
  }

  if (!latest) return null
  const { type, raw } = latest
  return { type, raw }
}

/**
 * Latest non-empty ```html``` fence (by end index in markdown), with its 0-based
 * index among all ```html``` fences in document order (matches preview DOM).
 */
export function extractLatestHtmlPreviewBlockWithIndex(
  markdown: string,
): { raw: string; blockIndex: number } | null {
  let latest: { raw: string; end: number; blockIndex: number } | null = null
  let blockIndex = 0
  HTML_PREVIEW_FENCE_INDEX_RE.lastIndex = 0
  for (const m of markdown.matchAll(HTML_PREVIEW_FENCE_INDEX_RE)) {
    const raw = m[1]?.trim()
    const end = (m.index ?? 0) + m[0].length
    if (raw && (!latest || end > latest.end)) {
      latest = { raw, end, blockIndex }
    }
    blockIndex += 1
  }
  return latest ? { raw: latest.raw, blockIndex: latest.blockIndex } : null
}

/**
 * All non-empty ```html``` fences in `markdown`, each with its 0-based index among
 * every ```html``` fence in document order (matches `blockIndex` in the rendered
 * preview / `postProcessMarkdownHtml`). Streaming-safe: only closed fences are returned.
 */
export function extractHtmlPreviewBlocksWithIndex(
  markdown: string,
): Array<{ raw: string; blockIndex: number }> {
  const blocks: Array<{ raw: string; blockIndex: number }> = []
  let blockIndex = 0
  HTML_PREVIEW_FENCE_INDEX_RE.lastIndex = 0
  for (const m of markdown.matchAll(HTML_PREVIEW_FENCE_INDEX_RE)) {
    const raw = m[1]?.trim()
    if (raw) blocks.push({ raw, blockIndex })
    blockIndex += 1
  }
  return blocks
}

/**
 * All ```html``` blocks in `markdown` — **including an unclosed final fence** (the
 * assistant often streams/persists the page as an open ```html fence with no closing
 * ```), each with its 0-based `blockIndex` in document order (matching the backend's
 * `extract_html_blocks` / the publish endpoint). The preview/selector variant of
 * `extractHtmlBlocks`: trailing prose after `</html>` is clipped and `raw` is trimmed,
 * so the selector shows the same clean document the preview renders and publishes.
 * Empty blocks are dropped from the list but still consume their `blockIndex`.
 */
export function extractAllHtmlPreviewBlocks(
  markdown: string,
): Array<{ raw: string; blockIndex: number }> {
  const blocks: Array<{ raw: string; blockIndex: number }> = []
  for (const block of scanHtmlBlocks(markdown)) {
    const raw = clipTrailingAfterHtmlClose(block.raw).trim()
    if (raw) blocks.push({ raw, blockIndex: block.blockIndex })
  }
  return blocks
}

/** Number of closed ```mermaid … ``` fences (same pattern as preview extraction). */
export function countClosedMermaidFences(markdown: string): number {
  if (!markdown.includes('```mermaid')) return 0
  return markdown.match(MERMAID_FENCE_RE)?.length ?? 0
}

/**
 * Number of closed ```html … ``` fences in the markdown.
 *
 * Streaming-safe: detection only triggers once both the opening ```html tag and
 * the closing ``` have arrived, even if they cross buffer/flush boundaries —
 * counting happens against the cumulative message content.
 */
export function countClosedHtmlFences(markdown: string): number {
  if (!markdown.includes('```html')) return 0
  return markdown.match(HTML_FENCE_GLOBAL_RE)?.length ?? 0
}

export function buildMermaidFixUserMessage(
  error: string,
  source: string,
): string {
  const trimmedSource = source.trim()
  return [
    'The Mermaid diagram in the preview failed to render. Please reply with a fully corrected Mermaid diagram in a single ```mermaid code block (valid syntax, complete nodes and edges).',
    '',
    'Parser error:',
    error,
    '',
    'Faulty Mermaid source:',
    '```mermaid',
    trimmedSource || '(empty)',
    '```',
  ].join('\n')
}

/** Subtitle in the preview panel header (file title for tabular / deck previews). */
export function previewPanelContentTitle(
  content: PreviewContent | null,
): string {
  if (!content) return ''
  if (
    content.type === 'spreadsheet' ||
    content.type === 'powerpoint' ||
    content.type === 'pdf'
  )
    return content.title
  return ''
}

export function resolveHtmlPreviewViewMode(
  raw: string,
  preferredMode: HtmlPreviewViewMode,
): HtmlPreviewViewMode {
  if (!isHtmlGenerationComplete(raw)) return 'code'
  return preferredMode
}

export function buildPreviewRendererAttrs(
  content: PreviewContent | null,
  options?: { htmlViewMode?: HtmlPreviewViewMode },
): Record<string, unknown> {
  if (content?.type === 'html') {
    return {
      viewMode: resolveHtmlPreviewViewMode(
        content.raw,
        options?.htmlViewMode ?? 'preview',
      ),
    }
  }
  if (content?.type === 'spreadsheet') {
    return {
      title: content.title,
      rows: content.rows,
      loading: content.loading,
      errorMessage: content.error,
      truncatedRows: content.truncatedRows,
      truncatedCols: content.truncatedCols,
    }
  }
  if (content?.type === 'powerpoint' || content?.type === 'pdf') {
    return {
      title: content.title,
      buffer: content.buffer,
      loading: content.loading,
      errorMessage: content.error,
    }
  }
  return {}
}
